import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { cloneDeep } from 'lodash';
import { shallow } from 'zustand/shallow';

import { usePopup } from '../../context';
import { CreateMemoryGameContentEditTemplate } from '../../components/Templates';
import { QuestionAnswerLanguageSelectPopup } from '../../components/Popups/QuestionAnswerLanguageSelectPopup';
import { getLanguageDetail } from '../../constants/languageDetails';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../models/DraftStore';
import { BalanceType, TtsLocale } from '../../models/Base';
import { useDraftStore } from '../../stores/DraftStore';
import { splitIntoWords } from '../../utils/string';
import { TranslateAnswerPopup } from '../../components/Popups';
import { getLocaleText, getUiLocale } from '../../utils/i18n';
import { useTranslation } from 'react-i18next';
import { TranslationLocale } from '../../i18n';

interface State {
  type: 'Question' | 'Answer' | undefined;
  index: number | undefined;
  createType: QuickCreateGameMode;
  gameContent: string;
  placeHolder: string | undefined;
  maxTextLength: number;
  selectedLanguage: TtsLocale | undefined;
  isUseTts: boolean;
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  hasAutoTranslate: boolean;
}

const CreateMemoryGameContentEditScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const location = useLocation();
  const [state, setState] = useState<State>({
    type: undefined,
    index: undefined,
    createType: QuickCreateGameMode.QuickMemory,
    gameContent: '',
    placeHolder: undefined,
    maxTextLength: 0,
    selectedLanguage: undefined,
    isUseTts: true,
    isNextDisabled: false,
    isExceedLimit: false,
    hasAutoTranslate: false,
  });

  const { mode } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    return { mode: _mode };
  });

  const draft = useDraftStore((store) => {
    if (state.index !== undefined) {
      return store.getGameDraft(state.index);
    }
    return null;
  }, shallow);

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  const locale = useMemo(() => {
    if (state.type === 'Question') {
      if (draft?.game.question.locale) {
        return draft?.game.question.locale;
      }
    } else if (state.type === 'Answer') {
      if (draft?.game.answer.locale) {
        return draft?.game.answer.locale;
      }
    }
    return useDraftStore.getState().getLocale();
  }, [draft?.game.answer.locale, draft?.game.question.locale, state.type]);

  useEffect(() => {
    if (locale) {
      setState((s) => ({ ...s, selectedLanguage: locale }));
    }
  }, [locale]);

  useEffect(() => {
    if (draft === undefined) {
      navigate('/create/memory-game/generated-games', { replace: true });
    } else {
      if (state.type === 'Question') {
        setState((s) => ({
          ...s,
          gameContent: draft?.game.question.text?.text ?? '',
          maxTextLength: draft?.game.question.text?.maxLength ?? 0,
          placeHolder: draft?.game.question.text?.placeholder,
          isUseTts: draft?.game.question.hasTts ?? true,
        }));
      } else {
        setState((s) => ({
          ...s,
          gameContent: draft?.game.answer.text?.text ?? '',
          maxTextLength: draft?.game.answer.text?.maxLength ?? 0,
          placeHolder: draft?.game.answer.text?.placeholder,
          isUseTts: draft?.game.answer.hasTts ?? true,
        }));
      }
    }
  }, [draft, navigate, state.type]);

  useEffect(() => {
    if (location.state) {
      const type = (location.state as { type: 'Question' | 'Answer' }).type;
      const index = (location.state as { index: number }).index;
      if (index === undefined) {
        navigate('/create/memory-game/generated-games', { replace: true });
      }
      setState((s) => ({
        ...s,
        type: type,
        index: index,
      }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [location.state, navigate]);

  const onSwitchTts = useCallback(() => {
    setState((s) => ({ ...s, isUseTts: !s.isUseTts }));
  }, []);

  const onClickOK = useCallback(() => {
    const gameDraft = cloneDeep(draft);
    if (gameDraft) {
      if (state.type === 'Question') {
        gameDraft.game.question.text.text = state.gameContent;
        gameDraft.game.question.hasTts = state.isUseTts;
        gameDraft.game.question.text.soundText = state.hasAutoTranslate
          ? gameDraft.game.answer.text.text
          : gameDraft.game.question.text.soundText;
        gameDraft.game.question.locale =
          state.selectedLanguage ?? gameDraft.game.question.locale;
      } else {
        gameDraft.game.answer.text.text = state.gameContent;
        gameDraft.game.answer.hasTts = state.isUseTts;
        gameDraft.game.answer.locale =
          state.selectedLanguage ?? gameDraft.game.answer.locale;
        gameDraft.locale = state.selectedLanguage ?? gameDraft.locale;
      }
      useDraftStore.getState().editGameDraft(state.index ?? 0, gameDraft);
    }
    navigate('/create/memory-game/generated-games', { replace: true });
  }, [
    draft,
    navigate,
    state.gameContent,
    state.hasAutoTranslate,
    state.index,
    state.isUseTts,
    state.selectedLanguage,
    state.type,
  ]);

  const onClickBack = useCallback(() => {
    navigate('/create/memory-game/generated-games', { replace: true });
  }, [navigate]);

  const onGameContentChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setState((s) => ({ ...s, gameContent: event.target.value }));
    },
    [],
  );

  const onSelectLanguage = useCallback(
    (language: TtsLocale) => {
      setState((s) => ({ ...s, selectedLanguage: language }));
      hidePopup();
    },
    [hidePopup],
  );

  const onClickLanguage = useCallback(() => {
    showPopup(
      <QuestionAnswerLanguageSelectPopup
        languageList={
          getQuickCreateGameModeProps(state.createType).languageList
        }
        selectedLanguage={state.selectedLanguage}
        onClickOK={hidePopup}
        onCancel={hidePopup}
        onSelectLanguage={onSelectLanguage}
      />,
    );
  }, [
    hidePopup,
    onSelectLanguage,
    showPopup,
    state.createType,
    state.selectedLanguage,
  ]);

  useEffect(() => {
    const isExceedLimit =
      splitIntoWords(state.gameContent, state.selectedLanguage ?? locale, {
        withSpace: false,
      }).length > state.maxTextLength;
    if (state.gameContent && !isExceedLimit) {
      setState((s) => ({ ...s, isNextDisabled: false, isExceedLimit: false }));
    } else {
      if (isExceedLimit) {
        setState((s) => ({ ...s, isExceedLimit: true }));
      }
      setState((s) => ({ ...s, isNextDisabled: true }));
    }
  }, [locale, state.selectedLanguage, state.gameContent, state.maxTextLength]);

  const onClickAudioBar = useCallback(() => {
    toast.success(
      t(`Listen to the text-to-speech when playing the created game.`),
    );
  }, [t]);

  const onAutoTranslate = useCallback(async () => {
    if (draft) {
      const translationText = await useDraftStore
        .getState()
        .autoTranslateAnswer(
          draft.game.answer.text.text,
          draft.game.answer.locale,
          getUiLocale(i18n.language as TranslationLocale),
        );
      if (translationText) {
        setState((s) => ({
          ...s,
          gameContent: translationText,
          hasAutoTranslate: true,
        }));
      }
    }
    hidePopup();
  }, [draft, hidePopup, i18n.language]);

  const onClickAutoTranslate = useCallback(() => {
    showPopup(
      <TranslateAnswerPopup
        language={getLocaleText(
          getUiLocale(i18n.language as TranslationLocale),
        )}
        currencyType={BalanceType.CreationToken}
        fee={1}
        onPrimary={() => {
          onAutoTranslate().catch(console.error);
        }}
        onCancel={hidePopup}
      />,
    );
  }, [hidePopup, i18n.language, onAutoTranslate, showPopup]);

  return (
    <CreateMemoryGameContentEditTemplate
      type={state.type}
      language={getLanguageDetail(state.selectedLanguage)?.displayName ?? ''}
      isUseTts={state.isUseTts}
      gameContent={{
        value: state.gameContent,
        placeHolder: state.placeHolder,
        onChange: onGameContentChange,
      }}
      textLength={
        splitIntoWords(state.gameContent, state.selectedLanguage ?? locale, {
          withSpace: false,
        }).length
      }
      maxTextLength={state.maxTextLength}
      isNextDisabled={state.isNextDisabled}
      isExceedLimit={state.isExceedLimit}
      onClickLanguage={onClickLanguage}
      onSwitchTts={onSwitchTts}
      onClickAutoTranslate={onClickAutoTranslate}
      onClickAudioBar={onClickAudioBar}
      onClickOK={onClickOK}
      onClickBack={onClickBack}
    />
  );
};
export default CreateMemoryGameContentEditScreen;
